import React from 'react';
import { Badge, BlockStack, InlineStack, Text } from '@shopify/polaris';

const FRAME_STYLE = {
  position: 'relative',
  width: '100%',
  border: '1px solid #8c9196',
  borderRadius: 8,
  overflow: 'hidden',
  background: '#f6f6f7',
  boxShadow: '0 1px 3px rgba(0,0,0,.12)',
};

const sceneStyle = (image, color) => ({
  height: '100%',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: image
    ? `center / cover no-repeat url("${image}")`
    : color,
  color: '#202223',
  fontWeight: 600,
  fontSize: 12,
  border: '1px solid rgba(32,34,35,.28)',
  overflow: 'hidden',
});

const Scene = ({ image, label, color = '#dbeafe' }) => (
  <div style={sceneStyle(image, color)}>
    {!image && label}
  </div>
);

const Measurement = ({ children }) => (
  <Text variant="bodySm" tone="subdued">{children}</Text>
);

const CoverPreview = ({ cover = {}, direction, images }) => {
  const width = Number(cover.width_px) || 4961;
  const height = Number(cover.height_px) || 3508;
  const dpi = Number(cover.dpi) || 300;
  const coverWidth = Number(cover.cover_width_cm)
    ? Number(cover.cover_width_cm) / 2.54 * dpi
    : Number(cover.cover_width_px) || width / 2;
  const coverHeight = Number(cover.cover_height_cm)
    ? Number(cover.cover_height_cm) / 2.54 * dpi
    : Number(cover.cover_height_px) || height;
  const outerMargin = Number.isFinite(Number(cover.outer_margin_cm))
    ? Number(cover.outer_margin_cm) / 2.54 * dpi
    : Math.max(0, width / 2 - (Number(cover.inner_gap_half_px) || 0) - coverWidth);
  const sideWidth = Math.min(50, Math.max(0, (coverWidth / width) * 100));
  const sideHeight = Math.min(100, Math.max(0, (coverHeight / height) * 100));
  const left = Math.max(0, (outerMargin / width) * 100);
  const right = Math.max(0, 100 - left - sideWidth);
  const top = Math.max(0, (100 - sideHeight) / 2);
  const leftIsCover = direction === 'rtl';

  return (
    <BlockStack gap="150">
      <InlineStack align="space-between">
        <Text fontWeight="semibold">Cover frame</Text>
        <Badge>{cover.variant || 'cover'}</Badge>
      </InlineStack>
      <div style={{ ...FRAME_STYLE, aspectRatio: `${width} / ${height}` }}>
        <div style={{ position: 'absolute', left: `${left}%`, top: `${top}%`, width: `${sideWidth}%`, height: `${sideHeight}%` }}>
          <Scene
            image={leftIsCover ? images[0] : images[images.length - 1]}
            label={leftIsCover ? 'Front cover' : 'Back cover'}
            color="#fde68a"
          />
        </div>
        <div style={{ position: 'absolute', left: `${right}%`, top: `${top}%`, width: `${sideWidth}%`, height: `${sideHeight}%` }}>
          <Scene
            image={leftIsCover ? images[images.length - 1] : images[0]}
            label={leftIsCover ? 'Back cover' : 'Front cover'}
            color="#fca5a5"
          />
        </div>
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: '50%',
            borderLeft: '1px dashed #6d7175',
          }}
        >
          <span style={{ fontSize: 10, color: '#6d7175', marginLeft: 4 }}>spine</span>
        </div>
      </div>
      <Measurement>
        {`${width} × ${height}px at ${dpi} DPI, frame ${Math.round(coverWidth)} × ${Math.round(coverHeight)}px`}
      </Measurement>
    </BlockStack>
  );
};

const SpreadPreview = ({
  title,
  width,
  height,
  leftLabel,
  rightLabel,
  leftImage,
  rightImage,
  outerMargin = 0,
  topMargin = 0,
  sceneWidth = width / 2,
  sceneHeight = height,
}) => (
  <BlockStack gap="150">
    <Text fontWeight="semibold">{title}</Text>
    <div
      style={{ ...FRAME_STYLE, aspectRatio: `${width} / ${height}` }}
    >
      <div
        style={{
          position: 'absolute',
          left: `${outerMargin / width * 100}%`,
          top: `${topMargin / height * 100}%`,
          width: `${sceneWidth / width * 100}%`,
          height: `${sceneHeight / height * 100}%`,
        }}
      >
        <Scene image={leftImage} label={leftLabel} color="#bfdbfe" />
      </div>
      <div
        style={{
          position: 'absolute',
          right: `${outerMargin / width * 100}%`,
          top: `${topMargin / height * 100}%`,
          width: `${sceneWidth / width * 100}%`,
          height: `${sceneHeight / height * 100}%`,
        }}
      >
        <Scene image={rightImage} label={rightLabel} color="#c7d2fe" />
      </div>
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: '50%',
          borderLeft: '1px dashed #6d7175',
        }}
      />
    </div>
    <Measurement>
      {`${width} × ${height}cm, scene ${sceneWidth} × ${sceneHeight}cm, outer/top margins ${outerMargin}/${topMargin}cm`}
    </Measurement>
  </BlockStack>
);

const SinglePagePreview = ({ montage = {}, images }) => {
  const page = montage.page || {};
  const contentWidth = Number(page.content_width_cm) || 20;
  const contentHeight = Number(page.content_height_cm) || 20;
  const width = contentWidth
    + (Number(page.margin_left_cm) || 0)
    + (Number(page.margin_right_cm) || 0);
  const height = contentHeight
    + (Number(page.margin_top_cm) || 0)
    + (Number(page.margin_bottom_cm) || 0);
  const horizontalMargin = width
    ? ((Number(page.margin_left_cm) || 0) / width) * 100
    : 0;
  const verticalMargin = height
    ? ((Number(page.margin_top_cm) || 0) / height) * 100
    : 0;

  return (
    <BlockStack gap="150">
      <Text fontWeight="semibold">Pristol inner page</Text>
      <div
        style={{
          ...FRAME_STYLE,
          aspectRatio: `${width} / ${height}`,
          padding: `${verticalMargin}% ${horizontalMargin}%`,
          maxWidth: 420,
          margin: '0 auto',
        }}
      >
        <Scene image={images[1]} label="Interior scene" color="#bfdbfe" />
      </div>
      <Measurement>
        {`${contentWidth} × ${contentHeight}cm content, margins ${page.margin_left_cm || 0}/${page.margin_right_cm || 0}/${page.margin_top_cm || 0}/${page.margin_bottom_cm || 0}cm`}
      </Measurement>
    </BlockStack>
  );
};

const MontageProfilePreview = ({
  profile,
  lane,
  direction = 'ltr',
  sceneImages = [],
}) => {
  const outputs = profile?.outputs || {};
  const rtl = direction === 'rtl';
  const images = sceneImages.length > 0 ? sceneImages : [];
  const readable = outputs.readable?.variants?.['41x20-nocover']
    || outputs.readable?.variants?.['41x20']
    || {};
  const booklet = outputs.booklet || {};

  return (
    <BlockStack gap="400">
      {(lane === 'hard_cover' || lane === 'pristol_express') && (
        <CoverPreview cover={outputs.cover} direction={direction} images={images} />
      )}

      {lane === 'hard_cover' && (
        <SpreadPreview
          title="Hard-cover inner spread"
          width={Number(readable.width_cm) || 41}
          height={Number(readable.height_cm) || 20}
          leftLabel={rtl ? 'Page 2' : 'Page 1'}
          rightLabel={rtl ? 'Page 1' : 'Page 2'}
          leftImage={images[2] || images[1]}
          rightImage={images[1] || images[2]}
          outerMargin={Number(readable.outer_margin_cm) || 0}
          topMargin={Number(readable.top_margin_cm) || 0}
          sceneWidth={Number(readable.scene_width_cm) || (Number(readable.width_cm) || 41) / 2}
          sceneHeight={Number(readable.scene_height_cm) || Number(readable.height_cm) || 20}
        />
      )}

      {lane === 'booklet' && (
        <>
        <SpreadPreview
          title="Booklet imposed cover spread"
          width={Number(booklet.width_cm) || 41}
          height={Number(booklet.height_cm) || 20}
          leftLabel={rtl ? 'Page 30' : 'Back cover'}
          rightLabel={rtl ? 'Page 1' : 'Front cover'}
          leftImage={rtl ? images[images.length - 1] : images[0]}
          rightImage={rtl ? images[0] : images[images.length - 1]}
          outerMargin={0}
        />
        <SpreadPreview
          title="Booklet imposed inner spread"
          width={Number(booklet.width_cm) || 41}
          height={Number(booklet.height_cm) || 20}
          leftLabel={rtl ? 'Page 2' : 'Page 2'}
          rightLabel={rtl ? 'Page 29' : 'Page 29'}
          leftImage={images[2] || images[1]}
          rightImage={images[images.length - 2] || images[2]}
          outerMargin={0}
        />
        </>
      )}

      {lane === 'pristol_express' && (
        <SinglePagePreview montage={outputs.montage} images={images} />
      )}
    </BlockStack>
  );
};

export default MontageProfilePreview;
